<template>
  <v-card class="domain-model-card mb-5 position-relative" id="domain-model">
    <div>
      <v-toolbar flat color="white" height="62" @click.stop @mousedown.stop>
        <v-col cols="4" class="pt-3 d-flex subheading font-weight-bold align-center">
          <div class="mx-2 d-flex">
            <v-icon color="primary">mdi-alpha-d-box</v-icon>&nbsp;
            Domain Model
          </div>
          <help-dialog-workflow-aggregates
            v-slot="{ openDialog }"
            content-class="dialog-top-right"
          >
            <v-btn variant="text" icon="mdi-help-circle-outline" @click="openDialog" class="mr-2"></v-btn>
          </help-dialog-workflow-aggregates>
        </v-col>
        <v-spacer />
        <v-tooltip location="top" open-delay="100">
          <template v-slot:activator="{ props }">
            <div id="domain-model-event-selector" class="event-select-wrapper" v-bind="props">
              <event-select width="100%" :eventId="route.params.eventId" />
            </div>
          </template>
          <span>Select event to filter Domain Model</span>
        </v-tooltip>
        <v-checkbox
          v-model="domainModelShowReadModels"
          @click="toggleDomainModelShowReadModels()"
          label="Show read models"
          hide-details
          class="mx-4"
        />
        <v-tooltip location="top" open-delay="500">
          <template v-slot:activator="{ props }">
            <span v-bind="props">
              <v-checkbox
                v-model="domainModelShowEntities"
                @click="toggleDomainModelShowEntities()"
                label="Show data fields"
                hide-details
                class="mx-4"
              ></v-checkbox>
            </span>
          </template>
          <span>Show related entities</span>
        </v-tooltip>
      </v-toolbar>

      <template v-for="(boundedContext, boundedContextIndex) in boundedContexts" :key="boundedContextIndex">
        <div v-if="boundedContext.name !== null" class="bounded-context mx-5 mt-5 mb-10">
          <v-tabs
            class="bounded-context-tabs align-center"
            v-model="boundedContextTabs[boundedContext.name]"
            density="compact"
            height="48"
            bg-color="grey-lighten-4"
            @click.stop
            @mousedown.stop
          >
            <v-text-field
              v-if="renameBoundedContextId === boundedContext.id"
              class="mx-5 pa-0"
              autofocus
              hide-details
              :rules="[value => (value.length <= 40) || 'Max 40 characters']"
              v-model="renameBoundedContextName"
              v-on:keyup.enter="$event.target.blur()"
              @focus="$event.target.select()"
              @blur="renameBoundedContext()"
              variant="outlined"
              density="compact"
            />
            <h3
              v-else
              class="px-5 align-self-center"
              @click="renameBoundedContextId = boundedContext.id; renameBoundedContextName = boundedContext.name"
            >
              {{ boundedContext.name }}
            </h3>

            <div v-show="renameBoundedContextId !== boundedContext.id">
              <v-tooltip location="top" open-delay="500">
                <template v-slot:activator="{ props }">
                  <v-tab v-bind="props" value="diagram" :width="24" :ripple="false">
                    <v-icon size="x-large">mdi-file-tree</v-icon>
                  </v-tab>
                </template>

                <span>
                  Click here to view a diagram of your domain model
                </span>
              </v-tooltip>
              <v-tooltip location="top" open-delay="500">
                <template v-slot:activator="{ props }">
                  <v-tab v-bind="props" value="code" :ripple="false">
                    <v-icon size="x-large">mdi-code-block-tags</v-icon>
                  </v-tab>
                </template>

                <span>
                  Click here to generate API code and documentation for the domain model in this bounded context
                </span>
              </v-tooltip>
            </div>
            <v-spacer></v-spacer>
            <h4 style="user-select: none;" class="text-right mx-5 text-grey align-self-center">Bounded Context</h4>
          </v-tabs>
          <div v-if="boundedContextTabs[boundedContext.name] === 'code'">
            <workflow-aggregates-generate-code :boundedContext="boundedContext"/>
          </div>

          <div v-else>
            <div v-if="!boundedContext.systems?.length" class="pa-5 d-flex">
              <span class="pt-3">No systems in this bounded context</span>
              <v-spacer></v-spacer>
              <v-btn
                icon="mdi-delete"
                @click="confirmDeleteBoundedContext(boundedContext.id)"
                :disabled="$store.state.isWorkflowDisabled"
              />
            </div>
            <domain-model-system-table
              v-else
              :boundedContext="boundedContext"
              :showEntities="domainModelShowEntities"
              :showReadModels="domainModelShowReadModels"
              :refreshTrigger="refreshKey"
              :generatorEventStoryFunc="generatorEventStoryFunc"
              :generatorReadModelFunc="generatorReadModelFunc"
            />
          </div>
        </div>
        <div v-if="boundedContext.name === null" class="ma-5">
          <domain-model-system-table
            :boundedContext="boundedContext"
            :showEntities="domainModelShowEntities"
            :showReadModels="domainModelShowReadModels"
            :refreshTrigger="refreshKey"
            :generatorEventStoryFunc="generatorEventStoryFunc"
            :generatorReadModelFunc="generatorReadModelFunc"
          />
        </div>
      </template>
      <v-dialog v-model="showDeleteDialog" max-width="500">
        <v-card>
          <v-card-title>Confirm Delete</v-card-title>
          <v-card-text>
            Are you sure you want to delete this bounded context?
            Any code generated for this bounded context will be deleted.
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn variant="text" @click="showDeleteDialog = false; deleteBoundedContext(deleteBoundedContextId)">
              Delete
            </v-btn>
            <v-btn variant="text" @click="showDeleteDialog = false">Cancel</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </v-card>
</template>

<script setup>
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { computed, nextTick, ref, watch } from 'vue'
import EventSelect from '@/components/EventSelect'
import DomainModelSystemTable from '@/components/DomainModelSystemTable'
import HelpDialogWorkflowAggregates from '@/components/HelpDialogWorkflowAggregates'
import WorkflowAggregatesGenerateCode from '@/components/WorkflowAggregatesGenerateCode'
import randomUUID from '@/utils/uuid'
import { cardHelpers } from '@/mixins/cardHelpers'
import { sanitizeCardDescription } from '@/utils/sanitize'
import { workflowHelpers } from '@/mixins/workflowHelpers'
import useDomainModel from '@/composables/useDomainModel'

const { generatorEventStoryFunc, generatorReadModelFunc } = defineProps({
  generatorEventStoryFunc: Function,
  generatorReadModelFunc: Function
})

const refreshKey = ref(0)
const showDeleteDialog = ref(false)
const boundedContextTabs = ref({})
const deleteBoundedContextId = ref(null)
const renameBoundedContextId = ref(null)
const renameBoundedContextName = ref('')

const route = useRoute()
const store = useStore()
const workflow = computed(() => store.state.workflow.workflow)
const showSidePanel = computed(() => store.state.showSidePanel)
const domainModelShowEntities = computed(() => store.state.domainModelShowEntities)
const domainModelShowReadModels = computed(() => store.state.domainModelShowReadModels)
const svgGraph = computed(() => structuredClone(store.getters['svgGraphData/svgGraphData']))
const events = computed(() => (workflow.value.eventsJson || []).filter((event) => event.type === 'bpmn:Task'))
const sortedEvents = computed(() => {
  return [...events.value].sort((a, b) => svgGraph.value[a.id].x - svgGraph.value[b.id].x)
})

const { getDataModelFieldsFromEvent } = useDomainModel()

const systems = computed(() => {
  const entities = []
  const { eventId } = route.params
  const filteredEvents = eventId ? sortedEvents.value.filter((event) => event.id === eventId) : sortedEvents.value

  for (const event of filteredEvents) {
    const entityCard = cardHelpers.methods.getEntityCard(event)
    let commandCard = cardHelpers.methods.getCommandCard(event)
    let queryCards = cardHelpers.methods.getQueryCards(event)
    const rootEntity = entityCard?.entityId
      ? workflow.value.dataModels?.find((entity) => entity.id === entityCard.entityId)
      : null

    const boundedContext = rootEntity ? rootEntity.boundedContext : entityCard?.boundedContext

    if (commandCard) {
      // adding eventId to be able to link to the event when generating code
      commandCard = { ...commandCard, eventId: event.id, dataModel: getDataModelFieldsFromEvent(event, commandCard.id) }
    }
    // adding eventId to be able to link to the event when generating code
    queryCards = queryCards.map((card) => {
      return { ...card, eventId: event.id, dataModel: getDataModelFieldsFromEvent(event, card.id) }
    })
    const index = entities.findIndex((item) => {
      if (!entityCard || !item.groupedBy || (boundedContext !== item.groupedBy.boundedContext)) {
        return false
      }

      if (item.groupedBy.type === 'linked-entity' && rootEntity) {
        return item.groupedBy.rootEntity.id === rootEntity.id
      }

      if (item.groupedBy.type === 'unlinked-text' && entityCard && entityCard.description) {
        return item.groupedBy.text === sanitizeCardDescription(entityCard.description)
      }

      return false
    })

    const laneIndex = workflow.value.lanes.findIndex((lane) => lane.id === event.laneId)
    const prevRole = Object.assign({ laneIndex }, workflow.value.lanes[laneIndex])

    if (index >= 0) {
      // group on prevRole inside one system, if the prevRole is in this
      // system, insert data after the prevRole otherwise insert at the end
      const roleIndex = entities[index].roles.findLastIndex(item => item.id === prevRole.id)
      if (roleIndex >= 0) {
        entities[index].actions.splice(roleIndex + 1, 0, commandCard)
        entities[index].queries.splice(roleIndex + 1, 0, queryCards)
        entities[index].events.splice(roleIndex + 1, 0, event)
        entities[index].roles.splice(roleIndex + 1, 0, prevRole)
      } else {
        entities[index].actions.push(commandCard)
        entities[index].queries.push(queryCards)
        entities[index].events.push(event)
        entities[index].roles.push(prevRole)
      }

      if (entityCard) {
        entities[index].entityCards.push(entityCard)
      }
    } else {
      entities.push({
        id: randomUUID(),
        actions: commandCard ? [commandCard] : [null],
        queries: [queryCards],
        events: event ? [event] : [],
        entityCards: entityCard ? [entityCard] : [],
        roles: [prevRole],
        groupedBy: workflowHelpers.methods.getSystemGroupedBy(rootEntity, entityCard)
      })
    }
  }

  return entities
})

const boundedContexts = computed(() => {
  const result = structuredClone(workflow.value.boundedContexts || [])
  const notInBC = { name: null, systems: [] }
  const bcMap = {}
  result.forEach(it => {
    bcMap[it.id] = it
  })

  systems.value.forEach((system, systemIndex) => {
    let bc = bcMap[system.groupedBy?.boundedContext]
    if (!bc) {
      bc = notInBC
    }

    if (!bc.systems) {
      bc.systems = []
    }

    const mappedSystem = {
      ...system,
      events: system.events.map((event, index) => ({ ...event, index })),
      index: systemIndex
    }

    bc.systems.push(mappedSystem)
  })

  // Place systems that don't have a bounded context at the bottom
  result.push(notInBC)

  return (route.params.eventId ? result.filter((bc) => !!bc.systems) : result).sort((a, b) => {
    if (a.name === null) {
      // Put the systems without a bounded context at the bottom
      return 1
    }

    if (!a.systems?.length || !b.systems?.length) {
      // Do not sort empty bounded contexts
      return 0
    }

    return svgGraph.value[a.systems[0].events[0].id].x - svgGraph.value[b.systems[0].events[0].id].x
  })
})

const confirmDeleteBoundedContext = (id) => {
  showDeleteDialog.value = true
  deleteBoundedContextId.value = id
}

const deleteBoundedContext = async (id) => {
  const bc = workflow.value.boundedContexts.find((it) => it.id === id)
  if (bc) {
    const name = bc.name
    const boundedContexts = workflow.value.boundedContexts.filter(it => it.id !== id)
    // TODO: delete all the files associated with this!
    await store.dispatch('boundedContexts/deleteBoundedContext', id)
    await store.dispatch('workflow/updateWorkflow', {
      projectId: workflow.value.projectId,
      input: {
        id: workflow.value.id,
        expectedVersion: workflow.value.version,
        boundedContexts,
        updateNote: {
          action: 'deleted bounded context',
          target: name
        }
      }
    })
  }
}

const renameBoundedContext = () => {
  const bcIndex = workflow.value.boundedContexts.findIndex((it) => it.id === renameBoundedContextId.value)
  let bc = null
  if (bcIndex >= 0) {
    bc = workflow.value.boundedContexts[bcIndex]
  }

  if (bc && renameBoundedContextName.value && bc.name !== renameBoundedContextName.value) {
    if (workflow.value.boundedContexts.find((it) => it.name === renameBoundedContextName.value)) {
      store.commit('snackbar/showMessage', {
        content: 'There is already a bounded context named: ' + renameBoundedContextName.value,
        timeout: 6000,
        color: 'red',
        location: 'center center'
      }, { root: true })
    } else {
      const newBoundedContexts = workflow.value.boundedContexts.filter((it) => it.id !== bc.id)
      newBoundedContexts.splice(bcIndex, 0, { id: bc.id, name: renameBoundedContextName.value })

      store.dispatch('workflow/updateWorkflow', {
        projectId: workflow.value.projectId,
        input: {
          id: workflow.value.id,
          expectedVersion: workflow.value.version,
          boundedContexts: newBoundedContexts,
          updateNote: {
            action: 'renamed bounded context',
            target: renameBoundedContextName
          }
        }
      })

      // Update boundedContextTabs
      boundedContextTabs.value[renameBoundedContextName.value] = boundedContextTabs.value[bc.name]
      delete boundedContextTabs.value[bc.name]
    }
  }
  renameBoundedContextId.value = null
  renameBoundedContextName.value = ''
}

const toggleDomainModelShowEntities = () => {
  store.commit('setDomainModelShowEntities', !domainModelShowEntities.value)
}

const toggleDomainModelShowReadModels = () => {
  store.commit('setDomainModelShowReadModels', !domainModelShowReadModels.value)
}

watch([() => showSidePanel, () => workflow.value.version, () => domainModelShowEntities, () => systems], async () => {
  await nextTick()
  refreshKey.value++
})

watch(() => boundedContextTabs, async () => {
  await nextTick()
  refreshKey.value++
}, { deep: true })

</script>

<style scoped>
.domain-model-card {
  margin-top: 40px;
}

.bounded-context {
  background-color: rgb(251, 251, 251);
  border: 2px solid rgb(54, 50, 50);
  border-radius: 10px;
  overflow: hidden;
}

.bounded-context-tabs .v-tab {
  margin: 0 2px;
  padding: 0;
  width: 32px;
  min-width: 32px;
}

.event-select-wrapper {
  flex-grow: 1;
  max-width: 300px;
}
</style>
